<template>
  <div v-if="project">
    <div class="field">
      <label class="label">ID</label>
      <div class="control">
        <input class="input" type="text" v-model="project.id" disabled />
      </div>
    </div>
    <div class="field">
      <label class="label">Name</label>
      <div class="control">
        <input class="input" type="text" v-model="project.name" />
      </div>
    </div>

    <!-- Properties Fields -->
    <div class="field">
      <label class="label">Description</label>
      <div class="control">
        <textarea class="textarea" v-model="project.properties.description"></textarea>
      </div>
    </div>

    <div class="field">
      <label class="label">Start Date</label>
      <div class="control">
        <input class="input" type="date" v-model="project.properties.start_date" />
      </div>
    </div>

    <div class="field">
      <label class="label">End Date</label>
      <div class="control">
        <input class="input" type="date" v-model="project.properties.end_date" />
      </div>
    </div>

    <div class="field">
      <label class="label">Project Logo</label>
      <div class="control">
        <div class="logo-upload-container">
          <div class="file-upload-wrapper">
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="file-input"
              @change="handleLogoUpload"
            />
            <button
              type="button"
              class="button file-upload-button"
              @click="$refs.fileInput.click()"
            >
              <span class="icon is-small">
                <i class="fas fa-upload"></i>
              </span>
              <span>Choose Logo</span>
            </button>
          </div>
          <div v-if="project.properties && project.properties.project_logo" class="logo-preview">
            <img
              :src="project.properties.project_logo"
              alt="Project Logo"
              class="logo-image"
              @error="handleImageError"
              @load="handleImageLoad"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Latitude</label>
      <div class="control">
        <input class="input" type="number" step="any" v-model.number="project.properties.lat" />
      </div>
    </div>

    <div class="field">
      <label class="label">Longitude</label>
      <div class="control">
        <input class="input" type="number" step="any" v-model.number="project.properties.lon" />
      </div>
    </div>

    <div class="field">
      <label class="label">Address</label>
      <div class="control">
        <input class="input" type="text" v-model="project.properties.address" />
      </div>
    </div>

    <div class="field">
      <label class="label">State</label>
      <div class="control">
        <input class="input" type="text" v-model="project.properties.state" />
      </div>
    </div>

    <div class="field">
      <label class="label">City</label>
      <div class="control">
        <input class="input" type="text" v-model="project.properties.city" />
      </div>
    </div>

    <div class="field">
      <label class="label">Country</label>
      <div class="control">
        <input class="input" type="text" v-model="project.properties.country" />
      </div>
    </div>

    <div class="field">
      <label class="label">Industry</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="project.properties.industry">
            <option :value="null"></option>
            <option value="construction">Construction</option>
            <option value="shipbuilding">Shipbuilding</option>
            <option value="solar">Solar</option>
            <option value="mining">Mining</option>
            <option value="film set">Film Set</option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Project Type</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="project.properties.project_type">
            <option :value="null"></option>
            <option value="factory construction">Factory Construction</option>
            <option value="renovation">Renovation</option>
            <option value="shipyard">Shipyard</option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Project Status</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="project.properties.project_status">
            <option :value="null"></option>
            <option value="planned">Planned</option>
            <option value="in progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Is Archived</label>
      <div class="control">
        <label class="checkbox">
          <input type="checkbox" v-model="project.properties.is_archived" />
        </label>
      </div>
    </div>

    <div class="field">
      <label class="label">Is Public</label>
      <div class="control">
        <label class="checkbox">
          <input type="checkbox" v-model="project.properties.is_public" />
        </label>
      </div>
    </div>

    <div class="field">
      <label class="label">Integration Configuration</label>
      <div class="control">
        <div v-for="(pair, index) in integrationPairs" :key="index" class="columns" style="margin-bottom: 0.5rem">
          <div class="column is-5">
            <input class="input" type="text" v-model="pair.key" placeholder="Key" />
          </div>
          <div class="column is-5">
            <input class="input" type="text" v-model="pair.value" placeholder="Value" />
          </div>
          <div class="column is-2">
            <button class="button is-danger is-small" @click="removeIntegrationPair(index)">Remove</button>
          </div>
        </div>
        <button class="button is-small" @click="addIntegrationPair" style="margin-top: 0.5rem">Add Key-Value Pair</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import toast from "@/utils/toast";

export default {
  name: "ProjectGeneralTab",
  props: {
    projectData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      integrationPairs: [],
    };
  },
  computed: {
    ...mapState(["selectedProject"]),
    project() {
      return this.projectData || this.selectedProject;
    },
  },
  watch: {
    project: {
      immediate: true,
      handler(newProject) {
        if (newProject && !newProject.properties) {
          this.$set(newProject, "properties", {});
        }
        this.updateIntegrationPairs();
      },
    },
    integrationPairs: {
      deep: true,
      handler() {
        this.updateIntegrationConfig();
      },
    },
  },
  methods: {
    updateIntegrationPairs() {
      if (this.project && this.project.properties && this.project.properties.integration_configuration) {
        this.integrationPairs = Object.keys(this.project.properties.integration_configuration).map(key => ({
          key: key,
          value: this.project.properties.integration_configuration[key],
        }));
      } else {
        this.integrationPairs = [];
      }
    },
    updateIntegrationConfig() {
      if (!this.project.properties) {
        this.$set(this.project, "properties", {});
      }
      const config = {};
      this.integrationPairs.forEach(pair => {
        if (pair.key && pair.key.trim()) {
          config[pair.key] = pair.value || "";
        }
      });
      this.$set(this.project.properties, "integration_configuration", Object.keys(config).length > 0 ? config : null);
    },
    addIntegrationPair() {
      this.integrationPairs.push({ key: "", value: "" });
    },
    removeIntegrationPair(index) {
      this.integrationPairs.splice(index, 1);
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      // File size check (10 MB limit)
      const maxFileSize = 10 * 1024 * 1024; // 10 MB
      if (file.size > maxFileSize) {
        toast.error("File size exceeds maximum allowed size (10 MB)");
        event.target.value = '';
        return;
      }

      // Stricter MIME type check
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select an image file (PNG, JPEG, or JPG)");
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!this.project.properties) {
          this.$set(this.project, "properties", {});
        }
        this.$set(this.project.properties, "project_logo", e.target.result);
      };
      reader.onerror = () => {
        toast.error("Error uploading logo");
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    },
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    handleImageLoad(event) {
      event.target.style.display = 'block';
    },
  },
  mounted() {},
};
</script>

<style scoped>
/* File Upload Button Styling */
.logo-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.file-upload-wrapper {
  position: relative;
  display: inline-block;
}

.file-input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.file-upload-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.file-upload-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.file-upload-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.file-upload-button .icon {
  margin-right: 0.5rem;
}

.logo-preview {
  display: inline-block;
  flex-shrink: 0;
}

.logo-image {
  max-width: 200px;
  max-height: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 5px;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.logo-image:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
